import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  deleteNotification,
} from "@/lib/api/notifications"
import { useAuthSession } from "@/hooks/use-auth-session"
import { useNotificationSSE } from "@/hooks/use-notification-sse"

export function useNotifications() {
  const { currentUser } = useAuthSession()
  const queryClient = useQueryClient()

  useNotificationSSE(!!currentUser)

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: currentUser ? getNotifications : skipToken,
    refetchInterval: 60_000,
  })

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  })

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  })

  const dbNotifications = data?.dbNotifications ?? []
  const eventInvitations = data?.eventInvitations ?? []
  const unreadCount =
    dbNotifications.filter((n) => !n.read && !n.read_at).length + eventInvitations.length

  return {
    dbNotifications,
    eventInvitations,
    unreadCount,
    isLoading,
    markRead: markReadMutation.mutateAsync,
    markAllRead: markAllReadMutation.mutateAsync,
    deleteNotification: deleteMutation.mutateAsync,
    isMarkingAllRead: markAllReadMutation.isPending,
  }
}
